import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ExclamationCircleFilled,
  EyeOutlined,
  LogoutOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
  StopOutlined,
  UnlockOutlined,
  UserDeleteOutlined,
} from "@ant-design/icons";
import { uuid } from "@ant-design/plots/es/utils";
import { Button, Dropdown, Tag } from "antd";
import { DotVerticalIcon } from "assets/icons";
import {
  doBlockUser,
  doSignOutByAdmin,
  getUserOnlinePage,
} from "stores/slices/user";

import ModalForm from "components/forms/components/modalForm";

import AppButton from "../../../components/button";
import AppTable from "../../../components/table";
import {
  STATUS_TYPE,
  USER_TYPE,
  WORKSPACE_USER_TYPE,
} from "../../../constants/appConstant";
import { AuthInfo } from "../../../stores/slices/auth";
import { setHeaderTitle, setIsRefresh } from "../../../stores/slices/layout";
import { getWorkspaceInfo } from "../../../stores/slices/workspace";
import { doChangeStatusWorkspaceUser } from "../../../stores/slices/workspaceUser";

import UserDetailForm from "./userDetailForm";

const UserOnlinePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const params = useParams();
  const authInfo = useSelector(AuthInfo);

  const dispatch = useDispatch();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isOpenModalChangeStatus, setIsOpenModalChangeStatus] = useState(false);
  const [selectedUserOnlineChangeStatus, setSelectedUserOnlineChangeStatus] =
    useState();
  const [isOpenModalLogout, setIsOpenModalLogout] = useState(false);
  const [selectedUserOnlineLogout, setSelectedUserOnlineLogout] = useState();
  const [isLogout, setIsLogout] = useState(false);

  const [isOpenModalBlockUser, setIsOpenModalBlockUser] = useState(false);
  const [isBocking, setIsBlocking] = useState(false);
  const [selectedUserBlock, setSelectedUserBlock] = useState();

  const [isView, setIsView] = useState(false);
  const [isOpenDetailFrm, setIsOpenDetailFrm] = useState(false);
  const [itemSelected, setItemSelected] = useState(null);
  const [workspaceInfo, setWorkspaceInfo] = useState(null);
  const [columns, setColumns] = useState([]);

  const [searchParam, setSearchParam] = useState({
    sortFieldNotices: "createdTime",
    sortDirection: "DESC",
    pageNumber: 1,
    pageSize: 10,
    workspaceId: params.id !== "all" ? params.id : null,
    workspaceUserStatuses: "ACTIVE",
  });

  const handleSearch = (selectedKeys, confirm) => {
    confirm();
  };

  const handleFilter = (selectedKeys, confirm) => {
    confirm();
  };

  const handleTableChange = (pagination, filters, sorter) => {
    let newSearchParam = {};
    filters.workspaceName &&
      (newSearchParam = {
        ...newSearchParam,
        workspaceName: filters.workspaceName[0],
      });

    filters.email &&
      (newSearchParam = {
        ...newSearchParam,
        email: filters.email[0],
      });

    filters.username &&
      (newSearchParam = { ...newSearchParam, username: filters.username[0] });

    filters.userType &&
      (newSearchParam = { ...newSearchParam, type: filters.userType });

    filters.status &&
      (newSearchParam = { ...newSearchParam, statuses: filters.status });

    if (sorter && sorter.columnKey && sorter.order) {
      newSearchParam = {
        ...newSearchParam,
        sortField: sorter.columnKey,
        sortDirection: sorter.order === "ascend" ? "ASC" : "DESC",
      };
    } else {
      newSearchParam = {
        ...newSearchParam,
        sortField: "createdTime",
        sortDirection: "DESC",
      };
    }

    newSearchParam = {
      ...newSearchParam,
      workspaceId: params.id !== "all" ? params.id : null,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
    };
    setSearchParam(newSearchParam);
  };

  const actionOptionItems = record => {
    return [
      {
        key: "0",
        label: <span>{t("user.viewAndEdit")}</span>,
        icon: <EyeOutlined />,
        disabled: record.status === STATUS_TYPE.DELETE,
        onClick: () => {
          setIsView(true);
          setItemSelected(record);
          setIsOpenDetailFrm(true);
        },
      },
      {
        key: "1",
        danger: true,
        label: <span>{t("user.logout")}</span>,
        icon: <LogoutOutlined />,
        disabled: authInfo.id === record.id,
        onClick: () => {
          setSelectedUserOnlineLogout(record);
          setIsOpenModalLogout(true);
        },
      },
      {
        key: "2",
        danger: true,
        label: <span>{t("user.deactivate")}</span>,
        icon: <UserDeleteOutlined />,
        disabled:
          record?.type === USER_TYPE.SUPER_ADMIN ||
          authInfo.id === record.id ||
          !record.workspace, // free user
        onClick: () => {
          setSelectedUserOnlineChangeStatus(record);
          setIsOpenModalChangeStatus(true);
        },
      },
      {
        key: "4",
        danger: true,
        label: record.blocked ? (
          <span>{t("user.unblockUserTitle")}</span>
        ) : (
          <span>{t("user.blockUserTitle")}</span>
        ),
        icon: record.blocked ? (
          <UnlockOutlined title={t("user.unblockUserTitle")} />
        ) : (
          <StopOutlined title={t("user.blockUserTitle")} />
        ),
        disabled:
          record.workspace?.status === STATUS_TYPE.DELETE ||
          authInfo.id === record.id || // me
          record.type === USER_TYPE.SUPER_ADMIN || // super admin
          authInfo.type !== USER_TYPE.SUPER_ADMIN || // !super admin
          record.workspace?.workspaceUser?.status === STATUS_TYPE.DELETE, // user deleted
        onClick: () => {
          setSelectedUserBlock(record);
          setIsOpenModalBlockUser(true);
        },
      },
      {
        key: "3",
        label: <span>{t("user.searchIpAddress")}</span>,
        icon: <SearchOutlined />,
        onClick: () => {
          window.open(
            `https://whatismyipaddress.com/ip/${record.lastIpAddress}`,
            "_blank"
          );
        },
      },
    ];
  };

  const generateTableColumns = () => {
    let cols = [];
    if (authInfo.type === USER_TYPE.SUPER_ADMIN) {
      cols.push({
        title: t("user.workspace"),
        width: 100,
        dataIndex: ["workspace", "name"],
        key: "workspaceName",
        sorter: true,
        isSearchable: true,
        searchPlaceholder:
          "Search " + t("user.workspace").toString().toLowerCase(),
        ellipsis: {
          showTitle: true,
        },
        render: (_, { workspace }) => (
          <>{workspace?.name ? workspace.name : "N/A"}</>
        ),
      });
    }
    cols = [
      ...cols,
      {
        title: t("user.userName"),
        width: 60,
        dataIndex: "username",
        key: "username",
        sorter: true,
        isSearchable: true,
        searchPlaceholder:
          "Search" + t("user.username").toString().toLocaleLowerCase(),
        ellipsis: {
          showTitle: true,
        },
        render: (_, record) => (
          <>
            {record && record.firstName && record.lastName
              ? `${record.firstName} ${record.lastName}`
              : `-`}
          </>
        ),
      },
      {
        title: t("user.userEmail"),
        width: 100,
        dataIndex: ["contact", "email"],
        key: "email",
        sorter: true,
        isSearchable: true,
        searchPlaceholder:
          "Search " + t("user.userEmail").toString().toLowerCase(),
        ellipsis: {
          showTitle: true,
        },
        render: (_, user) => <>{user?.email ? user.email : "-"}</>,
      },
      {
        title: t("user.userType"),
        width: 60,
        dataIndex: ["workspace", "userType"],
        key: "userType",
        filters: [
          {
            text: t("role.admin"),
            value: "ADMIN",
          },
          {
            text: t("role.operator"),
            value: "OPERATOR",
          },
        ],
        render: (_, record) => (
          <Tag
            color={
              record.type === USER_TYPE.SUPER_ADMIN
                ? "blue"
                : record.workspace?.workspaceUser?.type ===
                  WORKSPACE_USER_TYPE.ADMIN
                ? "success"
                : record.workspace?.workspaceUser?.type ===
                  WORKSPACE_USER_TYPE.OPERATOR
                ? "warning"
                : "purple"
            }
            bordered={false}
          >
            {record.type === USER_TYPE.SUPER_ADMIN
              ? t("role.superAdmin").toUpperCase()
              : record.workspace?.workspaceUser?.type ===
                WORKSPACE_USER_TYPE.ADMIN
              ? t("role.admin").toUpperCase()
              : record.workspace?.workspaceUser?.type ===
                WORKSPACE_USER_TYPE.OPERATOR
              ? t("role.workspaceOperator").toUpperCase()
              : t("role.user").toUpperCase()}
          </Tag>
        ),
      },
      {
        title: t("user.ipAddress"),
        width: 65,
        dataIndex: "lastIpAddress",
        key: "lastIpAddress",
        sorter: true,
        render: (_, { lastIpAddress }) => <>{lastIpAddress || "-"}</>,
      },
      {
        title: t("user.actions"),
        width: 40,
        render: (_, record) => {
          return (
            <Dropdown
              trigger={["click"]}
              menu={{
                items: actionOptionItems(record),
              }}
              placement="bottomRight"
            >
              <Button type="text" shape="circle" size="middle">
                <DotVerticalIcon />
              </Button>
            </Dropdown>
          );
        },
      },
    ];
    setColumns(cols);
  };

  const handleChangeStatus = async itemSelected => {
    const { payload } = await dispatch(
      doChangeStatusWorkspaceUser({
        workspaceId: itemSelected.workspace ? itemSelected.workspace.id : null,
        userId: itemSelected.id,
        status:
          itemSelected.workspace?.workspaceUser?.status === STATUS_TYPE.INACTIVE
            ? STATUS_TYPE.ACTIVE
            : STATUS_TYPE.INACTIVE,
      })
    );
    setIsOpenModalChangeStatus(false);
    setSelectedUserOnlineChangeStatus();
    if (payload && payload.status === 200) {
      dispatch(setIsRefresh(uuid()));
      setIsDeleting(false);
    } else {
      setIsDeleting(false);
    }
  };

  const handleLogoutUserOnline = async userSelected => {
    const { payload } = await dispatch(doSignOutByAdmin(userSelected.id));

    if (payload && payload.status === 200) {
      dispatch(setIsRefresh(uuid()));
      setIsOpenModalLogout(false);
      setIsLogout(false);
      setSelectedUserOnlineLogout(null);
    } else {
      setIsLogout(false);
    }
  };
  const handleBlock = async record => {
    const { payload } = await dispatch(
      doBlockUser({
        id: record.id,
        isBlocked: !record.blocked,
      })
    );
    if (payload && payload.status === 200) {
      setIsOpenModalBlockUser(false);
      dispatch(setIsRefresh(uuid()));
      setIsBlocking(false);
      !record.blocked && toast.success(t("toast.block_success"));
      record.blocked && toast.success(t("toast.unblock_success"));
      setSelectedUserBlock(null);
    } else {
      setIsBlocking(false);
      setSelectedUserBlock(null);
    }
  };

  useEffect(() => {
    dispatch(setHeaderTitle(["userManagement", "usersOnlineNow"]));

    if (authInfo.workspaceId && params.id !== "all" && params.id !== "null") {
      getWorkspaceDetail(
        params.id === "default" ? authInfo.workspaceId : params.id
      ).then(res => {
        if (
          res === "WORKSPACE_NOT_FOUND" ||
          res === "CAN_ONLY_VIEW_YOUR_OWN_INFORMATION"
        ) {
          navigate("/admin/workspaces");
          return;
        }
        console.log("getWorkspaceDetail", res);
        setWorkspaceInfo(res);
      });
    }
  }, []);

  useEffect(() => {
    generateTableColumns();
  }, [workspaceInfo?.id]);

  const getWorkspaceDetail = async workspaceId => {
    const { payload } = await dispatch(getWorkspaceInfo(workspaceId));
    return payload;
  };

  const handleTitleDelete = () => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <QuestionCircleOutlined style={{ color: "red", fontSize: 24 }} />
        <span style={{ marginLeft: 10, fontSize: 16 }}>
          {t("user.deactivateUserTitle")}
        </span>
      </div>
    );
  };

  const handleTitleBlockUser = () => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <ExclamationCircleFilled style={{ color: "#faad14", fontSize: 24 }} />
        <span style={{ marginLeft: 10, fontSize: 16 }}>
          {selectedUserBlock?.blocked
            ? t("user.unblockUserTitle")
            : t("user.blockUserTitle")}
        </span>
      </div>
    );
  };

  const handleTitleLogout = () => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <QuestionCircleOutlined style={{ color: "red", fontSize: 24 }} />
        <span style={{ marginLeft: 10, fontSize: 16 }}>
          {t("user.forceUserToLogoutTitle")}
        </span>
      </div>
    );
  };
  return (
    <>
      {params.id === "null" ? (
        <div style={noticeStyles.mainContainer}>
          <div
            style={{
              fontSize: 20,
              fontWeight: "bold",
              textAlign: "center",
              marginTop: 50,
            }}
          >
            {t("user.workspaceEmptyMsg")}
          </div>
          <AppButton
            label={t("user.goToWorkspaceManagement")}
            color={"primary"}
            type="primary"
            onClick={() => {
              navigate("/admin/workspaces");
            }}
            style={{
              ...noticeStyles.buttonAdd,
              marginLeft: "auto",
              marginRight: "auto",
            }}
          />
        </div>
      ) : (
        <div style={noticeStyles.mainContainer}>
          <AppTable
            api={getUserOnlinePage(searchParam)}
            columns={columns}
            searchParam={searchParam}
            handleSearch={handleSearch}
            handleFilter={handleFilter}
            // isRefresh={isRefresh}
            handleTableChange={handleTableChange}
            setSearchParam={setSearchParam}
          />
        </div>
      )}
      <ModalForm
        isOpen={isOpenModalChangeStatus}
        type="confirm"
        title={handleTitleDelete()}
        onCancel={() => {
          setIsOpenModalChangeStatus(false);
        }}
        onSubmit={() => {
          handleChangeStatus(selectedUserOnlineChangeStatus);
        }}
        okType={"danger"}
        danger={true}
        maskClosable={false}
        footer={[
          <AppButton
            key="button"
            label={t("button.cancel")}
            color={"grey"}
            loading={isDeleting}
            disabled={isDeleting}
            htmlType="button"
            onClick={() => {
              setIsOpenModalChangeStatus(false);
            }}
          />,
          <AppButton
            key="button"
            color="red"
            label={t("button.deactivate")}
            loading={isDeleting}
            disabled={isDeleting}
            onClick={() => {
              setIsDeleting(true);
              handleChangeStatus(selectedUserOnlineChangeStatus);
            }}
          />,
        ]}
      >
        <div style={{ height: 50, marginLeft: 35, fontSize: 14 }}>
          {t("user.deactivateUserMsg")}
        </div>
      </ModalForm>

      <ModalForm
        isOpen={isOpenModalLogout}
        type="confirm"
        title={handleTitleLogout()}
        onCancel={() => {
          setIsOpenModalLogout(false);
        }}
        okType={"danger"}
        danger={true}
        maskClosable={false}
        footer={[
          <AppButton
            key="button"
            label={t("button.cancel")}
            color={"grey"}
            loading={isLogout}
            disabled={isLogout}
            htmlType="button"
            onClick={() => {
              setIsOpenModalLogout(false);
            }}
          />,
          <AppButton
            key="button"
            color="red"
            label={t("button.yes")}
            loading={isLogout}
            disabled={isLogout}
            onClick={() => {
              setIsLogout(true);
              handleLogoutUserOnline(selectedUserOnlineLogout);
            }}
          />,
        ]}
      >
        <div style={{ height: 50, marginLeft: 35, fontSize: 14 }}>
          {t("user.forceUserToLogoutMsg")}
        </div>
      </ModalForm>

      <ModalForm
        isOpen={isOpenModalBlockUser}
        type="confirm"
        title={handleTitleBlockUser()}
        onCancel={() => {
          setIsOpenModalBlockUser(false);
        }}
        danger={true}
        maskClosable={false}
        footer={[
          <AppButton
            key="button"
            label={t("button.cancel")}
            color={"grey"}
            loading={isBocking}
            disabled={isBocking}
            htmlType="button"
            onClick={() => {
              setIsOpenModalBlockUser(false);
            }}
          />,
          <AppButton
            key="submit"
            htmlType="submit"
            color={selectedUserBlock?.blocked ? "primary" : "red"}
            label={
              selectedUserBlock?.blocked
                ? t("button.unblock")
                : t("button.block")
            }
            loading={isBocking}
            disabled={isBocking}
            onClick={() => {
              setIsBlocking(true);
              handleBlock(selectedUserBlock);
            }}
          />,
        ]}
      >
        <div style={{ height: 50, marginLeft: 35, fontSize: 14 }}>
          {selectedUserBlock?.blocked
            ? t("user.unblockUserMsg")
            : t("user.blockUserMsg")}
        </div>
      </ModalForm>

      <ModalForm
        title={isView ? t("user.viewUser") : t("user.updateUser")}
        onCancel={() => setIsOpenDetailFrm(false)}
        destroyOnClose={true}
        isOpen={isOpenDetailFrm}
        labelSubmit={t("label.save")}
        customWidth={500}
        footer={false}
        maskClosable={false}
      >
        <UserDetailForm
          isViewFrm={isView}
          itemSelected={itemSelected}
          workspaceInfo={workspaceInfo}
          onCancel={() => {
            setItemSelected(null);
            setIsOpenDetailFrm(false);
          }}
          onUpdate={() => {
            setIsView(false);
          }}
          onSuccess={() => {
            setItemSelected(null);
            setIsOpenDetailFrm(false);
            dispatch(setIsRefresh(uuid()));
          }}
        />
      </ModalForm>
    </>
  );
};

const noticeStyles = {
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    // gap: "24px",
    marginBottom: "24px",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "24px",
  },
  buttonAdd: {
    minWidth: "auto",
  },
};

export default UserOnlinePage;
